import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileVisible, setMobileVisible] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setMobileVisible(true);
        });
      });
    } else {
      document.body.style.overflow = '';
      setMobileVisible(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileVisible(false);
    setTimeout(() => setMobileMenuOpen(false), 300);
  };

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'Blog', path: '/hipobuy-spreadsheet-guide' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/80 backdrop-blur-lg border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link
              to="/"
              className="flex items-center gap-2 text-foreground-950 hover:text-primary-500 transition-colors duration-200"
            >
              <div className="w-9 h-9 flex items-center justify-center">
                <i className="ri-file-list-3-line text-2xl text-primary-500"></i>
              </div>
              <span className="font-heading text-xl font-bold whitespace-nowrap">
                Hipobuy Sheet
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
                >
                  {link.label}
                </Link>
              ))}

              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-background-100 border border-background-200/70 rounded-full text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Categories
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                  </div>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 border border-background-200/70 rounded-2xl shadow-lg overflow-hidden z-50 max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-500 transition-colors duration-150 whitespace-nowrap"
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-base`}></i>
                        </div>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Hipobuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>

            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer"
              aria-label="Open menu"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-menu-line text-xl text-foreground-950"></i>
              </div>
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          <div
            className={`absolute inset-0 bg-foreground-950/40 backdrop-blur-sm transition-opacity duration-300 ${mobileVisible ? 'opacity-100' : 'opacity-0'}`}
            onClick={closeMobileMenu}
          />

          <div
            className={`absolute top-0 right-0 w-[88%] max-w-sm h-full bg-background-50 transition-transform duration-300 ease-out ${
              mobileVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-5 py-4 border-b border-background-100">
                <Link
                  to="/"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-2 text-foreground-950"
                >
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-file-list-3-line text-xl text-primary-500"></i>
                  </div>
                  <span className="font-heading text-lg font-bold whitespace-nowrap">Hipobuy Sheet</span>
                </Link>
                <button
                  onClick={closeMobileMenu}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer"
                  aria-label="Close menu"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-close-line text-xl text-foreground-950"></i>
                  </div>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain">
                <div className="px-5 py-6 space-y-1">
                  <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-widest mb-3 px-1">Navigation</p>
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={closeMobileMenu}
                      className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-500 rounded-xl transition-all duration-150"
                    >
                      <div className="w-5 h-5 flex items-center justify-center text-foreground-400">
                        {link.label === 'Home' ? (
                          <i className="ri-home-4-line text-base"></i>
                        ) : (
                          <i className="ri-article-line text-base"></i>
                        )}
                      </div>
                      {link.label}
                    </Link>
                  ))}
                </div>

                <div className="px-5 py-2">
                  <div className="border-t border-background-100"></div>
                </div>

                <div className="px-5 pb-4">
                  <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-widest mb-3 px-1">Product Categories</p>
                  <div className="space-y-0.5">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={closeMobileMenu}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-500 rounded-xl transition-all duration-150 group"
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500 group-hover:bg-primary-100 group-hover:text-primary-500 transition-colors duration-200 flex-shrink-0">
                          <i className={`${cat.icon} text-base`}></i>
                        </div>
                        <span className="flex-1 whitespace-nowrap">{cat.name}</span>
                        <div className="w-4 h-4 flex items-center justify-center text-foreground-300 group-hover:text-primary-400 transition-colors duration-200">
                          <i className="ri-arrow-right-s-line text-sm"></i>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div className="px-5 py-4 border-t border-background-100 space-y-2.5">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Hipobuy Sheet
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line text-sm"></i>
                  </div>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}